import { useState } from "react";
import { 
  Shield, 
  Users, 
  CheckCircle, 
  Phone,
  Building,
  FileText,
  Search,
  Award,
  AlertTriangle,
  DollarSign,
  HeartHandshake,
  Loader2,
  CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import useSEO from "@/hooks/useSEO";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InsuranceAgents() {
  useSEO();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    reason: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/api/insurance-referrals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to submit request");
      }

      setIsSubmitted(true);
      setFormData({ name: "", phone: "", email: "", city: "", reason: "", message: "" });
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const reasons = [
    {
      icon: DollarSign,
      title: "Looking For Better Rates",
      description: "Insurance premiums vary significantly between carriers. A good agent can help you find competitive rates without sacrificing coverage."
    },
    {
      icon: Shield,
      title: "Need Better Storm Coverage",
      description: "Not all policies handle storm damage the same way. Understanding your coverage before a storm hits is critical."
    },
    {
      icon: AlertTriangle,
      title: "Had A Bad Claims Experience",
      description: "If your current carrier made the claims process difficult, it might be time to find an agent who advocates for you."
    },
    {
      icon: HeartHandshake,
      title: "Want A Local Agent Who Understands Minnesota",
      description: "Minnesota weather is unique. Local agents understand hail seasons, ice dams, and the specific risks our homes face."
    }
  ];

  const whatToLookFor = [
    "Experience with Minnesota storm damage claims",
    "Clear explanation of deductibles and coverage limits",
    "Understanding of Actual Cash Value vs Replacement Cost policies",
    "Responsive communication when you have questions",
    "Willingness to review your policy annually",
    "Knowledge of local building costs and contractor pricing"
  ];

  const questions = [
    "What does my policy cover for hail and wind damage?",
    "What is my deductible for storm-related claims?",
    "Do I have Actual Cash Value or Replacement Cost coverage?",
    "How will filing a claim affect my premiums?",
    "What documentation do I need after a storm?",
    "How quickly can I expect a response if I need to file a claim?"
  ];

  const reasonOptions = [
    { value: "better-rates", label: "Looking for better rates" },
    { value: "better-coverage", label: "Need better storm coverage" },
    { value: "bad-experience", label: "Had a bad claims experience" },
    { value: "local-agent", label: "Want a local agent who understands Minnesota" },
    { value: "other", label: "Other reason" }
  ];

  return (
    <div data-testid="insurance-agents-page" className="">
      {/* Hero Section */}
      <section className="bg-navy py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Search className="w-4 h-4" />
              Insurance Resources
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-tight mb-6">
              Looking For A New Homeowner's Insurance Agent?
            </h1>
            
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Whether you're shopping for better coverage, lower rates, or simply want an agent 
              who understands Minnesota storm damage, we may be able to point you in the right direction.
            </p>

            <a href="#referral-form">
              <Button className="bg-orange hover:bg-orange-hover text-white px-8 py-4 text-lg">
                <Phone className="w-5 h-5 mr-2" />
                Request An Agent Referral
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why People Switch Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-4">
              Why Homeowners Look For A New Insurance Agent
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              There are many good reasons to evaluate your current coverage and consider 
              working with a different agent or carrier.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reasons.map((reason) => (
              <div 
                key={reason.title}
                className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:border-orange/30 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-orange/10 rounded-xl flex items-center justify-center mb-4">
                  <reason.icon className="w-6 h-6 text-orange" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">
                  {reason.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What To Look For Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-6">
                What To Look For In A Good Insurance Agent
              </h2>
              
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                The right insurance agent does more than sell you a policy. They help you 
                understand your coverage, advocate for you during claims, and ensure you're 
                properly protected before storms hit.
              </p>

              <div className="space-y-4">
                {whatToLookFor.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-orange flex-shrink-0 mt-0.5" />
                    <span className="text-navy">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <h3 className="font-heading text-xl font-semibold text-navy mb-6">
                Questions To Ask A Potential Agent
              </h3>
              
              <div className="space-y-4">
                {questions.map((question, idx) => (
                  <div key={question} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-orange font-bold">{idx + 1}.</span>
                    <span className="text-gray-700">{question}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Can Help Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-navy rounded-2xl p-8 md:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-6">
                  How We Can Help
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed mb-6">
                  Over the years, we've built relationships with insurance professionals across 
                  Minnesota who specialize in homeowner's coverage. Many of them understand 
                  storm damage claims and can help ensure you have the right protection.
                </p>
                <p className="text-gray-400 leading-relaxed mb-8">
                  If you're looking for an insurance agent referral, let us know. We're happy 
                  to point you toward professionals we trust - no obligation, just a helpful connection.
                </p>

                <div className="flex items-center gap-4 p-4 bg-navy-light border border-navy-border rounded-lg">
                  <div className="w-12 h-12 bg-orange/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-orange" />
                  </div>
                  <div>
                    <div className="text-white font-medium">No Cost To You</div>
                    <div className="text-gray-400 text-sm">We don't charge for referrals - we're just here to help.</div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  { icon: Users, text: "Agents who understand Minnesota weather and storm claims" },
                  { icon: FileText, text: "Professionals who explain coverage clearly, not confusingly" },
                  { icon: Shield, text: "Help finding competitive rates without sacrificing protection" },
                  { icon: Building, text: "Local agents invested in Minnesota communities" }
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-4 bg-navy-light border border-navy-border rounded-lg p-4">
                    <div className="w-10 h-10 bg-orange/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-orange" />
                    </div>
                    <span className="text-white">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Agent Referral Form Section */}
      <section id="referral-form" className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-4">
              Request An Insurance Agent Referral
            </h2>
            <p className="text-gray-600 text-lg">
              Fill out the form below and we'll connect you with trusted insurance 
              professionals in Minnesota.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
            {isSubmitted ? (
              <div data-testid="referral-success" className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-navy mb-2">
                  Request Received!
                </h3>
                <p className="text-gray-600 mb-6">
                  Thank you for reaching out. We'll review your request and get back to you 
                  with insurance agent recommendations soon.
                </p>
                <Button 
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="border-navy text-navy hover:bg-navy hover:text-white"
                >
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="insurance-referral-form" className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-navy font-medium">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="referral-name-input"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className="border-gray-300 focus:border-orange focus:ring-orange"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-navy font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="referral-phone-input"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      required
                      className="border-gray-300 focus:border-orange focus:ring-orange"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-navy font-medium">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="referral-email-input"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                      className="border-gray-300 focus:border-orange focus:ring-orange"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-navy font-medium">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      data-testid="referral-city-input"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Your city in Minnesota"
                      required
                      className="border-gray-300 focus:border-orange focus:ring-orange"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason" className="text-navy font-medium">Why Are You Looking For A New Agent? *</Label>
                  <select
                    id="reason"
                    name="reason"
                    data-testid="referral-reason-select"
                    value={formData.reason}
                    onChange={handleChange}
                    required
                    className="w-full h-10 px-3 py-2 rounded-md border border-gray-300 bg-white text-navy focus:border-orange focus:ring-1 focus:ring-orange focus:outline-none"
                  >
                    <option value="">Select a reason...</option>
                    {reasonOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-navy font-medium">
                    Additional Details <span className="text-gray-400 font-normal">(Optional)</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="referral-message-input"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about what you're looking for in an insurance agent..."
                    rows={4}
                    className="border-gray-300 focus:border-orange focus:ring-orange resize-none"
                  />
                </div>

                {error && (
                  <div data-testid="referral-error" className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="referral-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-orange hover:bg-orange-hover text-white py-4 text-lg font-medium"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5 mr-2" />
                      Request Insurance Agent Referral
                    </>
                  )}
                </Button>

                <p className="text-center text-gray-500 text-sm">
                  Your information is kept private. We'll only share it with insurance professionals 
                  who can help with your request.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
